import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { endpoints, type GroupResponse } from '@/api'
import { ApiError } from '@/api/client-exports'

/**
 * The organizer's groups, and which one they are looking at (SPEC §8.9).
 *
 * Loaded once per session rather than per screen. Every page under /admin needs
 * the list — the navigation rail, the switcher, the group's name in the header
 * — and asking on each one would make moving between them cost a round trip
 * each time.
 */
export const useGroupContextStore = defineStore('groupContext', () => {
  const groups = ref<GroupResponse[]>([])
  const currentId = ref<string | null>(null)

  /** True once the server has been asked. False means "not yet known". */
  const isResolved = ref(false)

  const error = ref<string | null>(null)

  /** Shared, so several components mounting together ask once. */
  let inFlight: Promise<void> | null = null

  const current = computed(() => groups.value.find((g) => g.id === currentId.value) ?? null)

  const hasGroups = computed(() => groups.value.length > 0)

  /** One group means no switcher and no choosing (SPEC §8.9). */
  const isSingleGroup = computed(() => groups.value.length === 1)

  async function ensureLoaded(): Promise<void> {
    if (isResolved.value) return
    if (inFlight) return inFlight

    inFlight = (async () => {
      try {
        groups.value = await endpoints.listGroups()
        isResolved.value = true
        error.value = null
      } catch (cause) {
        // Left unresolved on failure, deliberately. Treating a dropped
        // connection as "no groups" would show somebody the set-up prompt for
        // a group they already have, and invite them to create a second.
        error.value =
          cause instanceof ApiError ? cause.userMessage : 'We could not load your groups.'
      } finally {
        inFlight = null
      }
    })()

    return inFlight
  }

  function select(groupId: string | null): void {
    currentId.value = groupId
  }

  /** Called after a group is created or changed, so the list is not stale. */
  async function refresh(): Promise<void> {
    isResolved.value = false
    await ensureLoaded()
  }

  function clear(): void {
    groups.value = []
    currentId.value = null
    isResolved.value = false
    error.value = null
  }

  return {
    groups,
    currentId,
    current,
    hasGroups,
    isSingleGroup,
    isResolved,
    error,
    ensureLoaded,
    select,
    refresh,
    clear,
  }
})
